// ============================================================
// NumPad.jsx — 직원 모바일 접수폼 전용 커스텀 키패드
//
// [왜 필요한가]
//  휴대폰 기본 키보드는 화면의 절반을 가리고, 숫자/문자 전환이 필요해
//  현장에서 한 손으로 쓰기 불편하다. 필요한 글자만 큰 버튼으로 깔아둔다.
//
// [사용법]
//   <NumPad mode="addr"  value={addr}  onChange={setAddr} />
//   <NumPad mode="floor" value={floor} onChange={setFloor} />
//   <NumPad mode="area"  value={area}  onChange={setArea} />
//
// [모드별 구성]
//   addr  : 상세주소   1~9 0 B 지 호 ← (B=B동, 지=지하, 호=호수)
//   floor : 해당층     1~9 0 - .   ← (-=지하, .=반층)
//   area  : 실평수     1~9 0 .     ←
//
// [연관] StaffIntake.jsx 가 이 컴포넌트를 3번 사용한다.
//        스타일은 StaffIntake.css 의 .np- 접두사 클래스.
// ============================================================
import React from "react";

// 모드별 버튼 배치.
// 배열의 한 줄이 화면의 한 줄이 된다. ""(빈 문자열)은 빈 칸.
const LAYOUTS = {
  // 번지 — 숫자와 하이픈만 (1666-46)
  addr: [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["0", "-", "⌫"],
    ["", "지움", ""],
  ],
  // 호수 — B(지하), 지(지하), 호 를 함께 쓴다 (B101호, 지1호)
  ho: [
    ["1", "2", "3", "B"],
    ["4", "5", "6", "지"],
    ["7", "8", "9", "호"],
    ["0", "-", "⌫", "지움"],
  ],
  floor: [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["-", "0", "."],
    ["⌫", "지움", ""],
  ],
  area: [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    [".", "0", "⌫"],
    ["", "지움", ""],
  ],
};

// onDone: 저장 버튼을 눌렀을 때 실행 (키패드를 닫는 용도)
//         넘기지 않으면 저장 버튼이 나타나지 않는다.
export default function NumPad({ mode = "addr", value = "", onChange, onDone }) {
  const layout = LAYOUTS[mode] || LAYOUTS.addr;

  // 버튼 하나를 눌렀을 때 값이 어떻게 변하는지 결정한다.
  //
  // [주의] '-' 의 뜻이 모드마다 다르다
  //   addr  모드: 번지 구분자   "1666-46"  → 뒤에 그대로 붙인다
  //   floor 모드: 지하 표시     "-1"       → 맨 앞에 붙인다
  //  같은 버튼이지만 하는 일이 달라 분기가 필요하다.
  const press = (key) => {
    if (!key) return;                        // 빈 칸은 무시
    if (key === "지움") return onChange("");   // 전체 삭제
    if (key === "⌫") return onChange(String(value).slice(0, -1)); // 한 글자 지우기

    const v = String(value);

    // 점(.)은 한 번만 — "3.5.2" 같은 값 방지
    if (key === "." && v.includes(".")) return;

    if (key === "-") {
      if (mode === "floor") {
        // 지하층: 맨 앞의 마이너스를 켜고 끈다
        return onChange(v.startsWith("-") ? v.slice(1) : "-" + v);
      }
      // 번지 구분자: 맨 앞이거나 이미 하이픈이 있으면 무시
      if (v === "" || v.endsWith("-") || v.includes("-")) return;
      return onChange(v + "-");
    }

    onChange(v + key);
  };

  return (
    <div className={`np np-${mode}`}>
      {/* 현재 입력값을 크게 보여준다. 비어 있으면 안내 문구 */}
      <div className="np-display">
        {value ? value : <span className="np-placeholder">입력하세요</span>}
      </div>

      <div className="np-keys">
        {layout.map((row, ri) => (
          <div className="np-row" key={ri}>
            {row.map((key, ki) => (
              <button
                key={`${ri}-${ki}`}
                type="button"                 // form 안에서 자동 제출되지 않게
                className={
                  "np-key" +
                  (key === "" ? " np-empty" : "") +
                  (key === "지움" ? " np-clear" : "") +
                  (key === "⌫" ? " np-back" : "") +
                  (["B", "지", "호"].includes(key) ? " np-alpha" : "")
                }
                disabled={key === ""}
                onClick={() => press(key)}
              >
                {key}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* 저장 버튼 — 누르면 키패드가 닫힌다.
          폰에서는 키패드가 화면을 많이 차지해 닫을 방법이 꼭 필요하다. */}
      {onDone && (
        <button type="button" className="np-done" onClick={onDone}>
          저장
        </button>
      )}
    </div>
  );
}